import { EngineAiOutput } from '@interfaces/ai'
import api from '../api'

const review = (
  publicId: string,
  testMode: boolean,
  performFullReview: boolean,
  onEvent: (event: string, data: any) => void,
  onError?: (error: { error: string } | string) => void
) => {
  fetch(`/api/ai/review/${publicId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ testMode, performFullReview }),
  })
    .then(async response => {
      if (!response.body) throw new Error('No response body received')

      if (response.status !== 200) {
        const error = (await response.json()) as { error: string }
        onError?.(error)
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (true) {
          const { done, value } = await reader.read()

          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const events = buffer.split('\n\n')

          // Keep the last potentially incomplete event in buffer
          buffer = events.pop() || ''

          for (const eventBlock of events) {
            if (!eventBlock.trim() || !eventBlock.startsWith('event: ')) continue

            const [eventLine, dataLine] = eventBlock.split('\n')
            if (eventLine?.startsWith('event: ') && dataLine?.startsWith('data: ')) {
              const event = eventLine.substring(7)
              const data = JSON.parse(dataLine.substring(6))

              onEvent(event, data)
            }
          }
        }

        // Handle any remaining complete event in buffer
        if (buffer.trim() && buffer.startsWith('event: ')) {
          const [eventLine, dataLine] = buffer.split('\n')
          if (eventLine?.startsWith('event: ') && dataLine?.startsWith('data: ')) {
            const event = eventLine.substring(7)
            const data = JSON.parse(dataLine.substring(6))
            onEvent(event, data)
          }
        }
      } catch (error: any) {
        onError?.(error)
      }
    })
    .catch(error => {
      onError?.(error)
    })
}

const streamingTextAction = async (
  payload: any,
  onEvent: (event: string, data: any) => void,
  onError?: (error: Record<string, any>) => void
): Promise<void> => {
  return fetch(`/api/ai/streaming-text-action`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
    .then(async response => {
      if (response.ok === false) {
        const errorData = (await response.json()) as { errorCode?: string; error?: string }
        onError?.(errorData)
        return
      }

      if (!response.body) {
        onError?.({ error: 'No response body received' })
        return
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (true) {
          const { done, value } = await reader.read()

          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const events = buffer.split('\n\n')

          // Keep the last potentially incomplete event in buffer
          buffer = events.pop() || ''

          for (const eventBlock of events) {
            if (!eventBlock.trim() || !eventBlock.startsWith('event: ')) continue

            const [eventLine, dataLine] = eventBlock.split('\n')
            if (eventLine?.startsWith('event: ') && dataLine?.startsWith('data: ')) {
              const event = eventLine.substring(7)
              const data = JSON.parse(dataLine.substring(6))

              onEvent(event, data)
            }
          }
        }

        // Handle any remaining complete event in buffer
        if (buffer.trim() && buffer.startsWith('event: ')) {
          const [eventLine, dataLine] = buffer.split('\n')
          if (eventLine?.startsWith('event: ') && dataLine?.startsWith('data: ')) {
            const event = eventLine.substring(7)
            const data = JSON.parse(dataLine.substring(6))
            onEvent(event, data)
          }
        }
      } catch (error) {
        onError?.({ error: error })
      }
    })
    .catch(error => {
      onError?.({ error: error })
    })
}

const updateRiskCardStatus = (payload: {
  documentPublicId: string
  documentVersionNumber: number
  blockId: string
  status: EngineAiOutput['riskCards'][number]['status']
  suggestion: EngineAiOutput['riskCards'][number]['suggestion']
}) => {
  return api.post(`/api/ai/update-risk-card-status`, payload)
}

const getSystemPrompt = async (slug: string) => {
  return api.get(`/api/ai/system-prompts/${slug}`).then(res => res.data)
}

const updateSystemPrompt = async (payload: { slug: string; body: string }) => {
  return api.patch(`/api/ai/system-prompts/${payload.slug}`, payload).then(res => res.data)
}

const checkActiveReview = async (publicId: string) => {
  return api
    .get(`/api/ai/review/${publicId}/resume`)
    .then(res => res.data)
    .catch(() => ({ active: false, events: [] }))
}

export const ai = {
  review,
  checkActiveReview,
  streamingTextAction,
  updateRiskCardStatus,
  getSystemPrompt,
  updateSystemPrompt,
}
